import { faUpRightFromSquare } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import type { Disclosures } from "~/components/site/disclosure";
import { Disclosure } from "~/components/site/disclosure";
import { ExternalLink } from "~/components/site/external-link";
import { item, Layout } from "~/components/site/layout";
import { SideNav } from "~/components/site/legal/sidenav";
import { EMAILS, ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

const { desc, path, title } = ROUTE_DATA.disclosures;

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

const disclosures: Disclosures[] = [
  {
    title: "District of Columbia",
    license: "MTR2376957",
    info: true,
    instructions: (
      <p className="m-0">
        If you have a complaint, please contact Brale Inc. at (844) 227-6212 or
        {` ${EMAILS.support}`}. If you still have an unresolved complaint
        regarding Brale Inc.&aposs money transmission activity, you may file it
        by contacting the Department at:
      </p>
    ),
    address: [
      "District of Columbia Department of Insurance",
      "Securities and Banking",
      "1050 First St NE, Suite 801",
      "Washington, DC 20002",
    ],
    fax: "(202) 727-8000",
    phone: "(202) 354-1085",
    email: "disbcomplaints@dc.gov",
    addInfo: (
      <ExternalLink
        className="w-fit flex items-center gap-x-3"
        title="View Mail-In Version"
        href="https://disb.dc.gov/sites/default/files/dc/sites/disb/publication/attachments/DISB%20Consumer%20Complaints_final%20fillable.pdf"
      >
        Consumer Complaint Form Mail-In Version
        <FontAwesomeIcon icon={faUpRightFromSquare} className="h-4 w-4" />
      </ExternalLink>
    ),
  },
  {
    title: "Florida",
    license: "FT230000475",
    info: true,
    instructions: (
      <p className="m-0">
        If you have a complaint, please contact Brale Inc. at (844) 227-6212 or
        {` ${EMAILS.support}`}. If you still have an unresolved complaint
        regarding Brale Inc. money transmission activity, you may file it by
        contacting the Florida Office of Financial Regulation at:
      </p>
    ),
    address: [
      "Florida Office of Financial Regulation",
      "Consumer Finance",
      "200 E Gaines St",
      "Tallahassee, FL 32399-0376",
    ],
    phone: "(850) 487-9687",
    addInfo: (
      <ExternalLink
        className="w-fit flex items-center gap-x-3"
        title="Submit a complaint or tip online"
        href="https://flofr.gov/sitePages/FileAComplaint.htm"
      >
        Submit a complaint or tip online
        <FontAwesomeIcon icon={faUpRightFromSquare} className="h-4 w-4" />
      </ExternalLink>
    ),
  },
  {
    title: "Illinois",
    license: "MT.0000472",
    info: true,
    instructions: (
      <p className="m-0">
        For complaints directly to the Illinois Division of Financial
        Institutions, please send correspondence to:
      </p>
    ),
    address: [
      "Illinois Division of Financial Institutions",
      "320 West Washington St, 3rd Floor",
      "Springfield, IL 62786",
    ],
    phone: "(888) 473-4858",
  },
];

export default function DisclosuresLayout(): JSX.Element {
  return (
    <Layout>
      <section className="grow prose dark:prose-invert max-w-none section-first">
        <div className="container grid lg:grid-cols-3 items-start lg:gap-16">
          <div className="lg:sticky top-16">
            <div className="eyebrow">Legal</div>
            <h1 className="h2 mt-0 mb-4">Disclosures</h1>
            <p className="mt-0 mb-12">
              <i>Last updated: 2023-08-16</i>
            </p>
            <SideNav />
          </div>
          <motion.div variants={item} className="col-span-2 mt-8">
            <p>
              Brale (Brale Inc., Brale Technologies) offers both technology and
              financial services products. Our financial services products
              operate with specific licenses which enable us to sell our
              financial services products in jurisdictions which license these
              operations.
            </p>
            <Disclosure region={"North America"} disclosures={disclosures} />
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
